import * as React from 'react'
import { cn } from '@/lib/utils'

interface DualProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
	approved: number
	rejected: number
	total: number
	showLabel?: boolean
	showPercentage?: boolean
	className?: string
	height?: string
}

const DualProgressBar = React.forwardRef<HTMLDivElement, DualProgressBarProps>(
	(
		{
			approved,
			rejected,
			total,
			showLabel = true,
			showPercentage = false,
			className,
			height = 'h-2',
			...props
		},
		ref
	) => {
		// Calculate percentages
		const approvedPercentage = total > 0 ? (approved / total) * 100 : 0
		const rejectedPercentage = total > 0 ? (rejected / total) * 100 : 0
		const processedPercentage = approvedPercentage + rejectedPercentage
		const pendingPercentage = 100 - processedPercentage

		// Calculate counts
		const pending = total - approved - rejected
		const processed = approved + rejected

		return (
			<div className={cn('space-y-2', className)} {...props}>
				{showLabel && (
					<div className="flex items-center justify-between text-sm">
						<div className="flex items-center gap-3">
							<span className="flex items-center gap-1">
								<span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
								<span className="text-emerald-700 font-medium">{approved} approved</span>
							</span>
							<span className="flex items-center gap-1">
								<span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
								<span className="text-red-700 font-medium">{rejected} rejected</span>
							</span>
							{pending > 0 && (
								<span className="flex items-center gap-1">
									<span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
									<span className="text-gray-600">{pending} pending</span>
								</span>
							)}
						</div>
						<span className="text-muted-foreground">
							{processed}/{total} processed
							{showPercentage && ` (${Math.round(processedPercentage)}%)`}
						</span>
					</div>
				)}

				<div
					ref={ref}
					role="progressbar"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={processedPercentage}
					aria-label={`${processed} of ${total} documents processed`}
					className={cn(
						'relative overflow-hidden rounded-full bg-gray-200',
						height
					)}
				>
					<div className="flex h-full">
						{/* Approved segment (green) */}
						{approvedPercentage > 0 && (
							<div
								className="bg-emerald-500 transition-all duration-300 ease-in-out"
								style={{ width: `${approvedPercentage}%` }}
								title={`${approved} approved (${Math.round(approvedPercentage)}%)`}
							/>
						)}

						{/* Rejected segment (red) */}
						{rejectedPercentage > 0 && (
							<div
								className="bg-red-500 transition-all duration-300 ease-in-out"
								style={{ width: `${rejectedPercentage}%` }}
								title={`${rejected} rejected (${Math.round(rejectedPercentage)}%)`}
							/>
						)}

						{/* Optional: Add a subtle divider between segments */}
						{approvedPercentage > 0 && rejectedPercentage > 0 && (
							<div className="absolute h-full w-px bg-white/30" 
								style={{ left: `${approvedPercentage}%` }} 
							/>
						)}
					</div>
				</div>

				{/* Compact mobile view - show only percentages */}
				{showLabel && (
					<div className="flex items-center gap-2 text-xs text-muted-foreground sm:hidden">
						<span className="text-emerald-600">{Math.round(approvedPercentage)}%</span>
						<span>•</span>
						<span className="text-red-600">{Math.round(rejectedPercentage)}%</span>
						{pending > 0 && (
							<>
								<span>•</span>
								<span className="text-gray-500">{Math.round(pendingPercentage)}%</span>
							</>
						)}
					</div>
				)}
			</div>
		)
	}
)

DualProgressBar.displayName = 'DualProgressBar'

export { DualProgressBar }